import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { useContent } from "../lib/useContent";
import { useSeo, Icon, LazyImg } from "../components/common";
import { PageHero } from "../components/PageHero";
import { BeforeAfter, CTABand } from "../components/blocks";
import { fileUrl } from "../lib/api";
import { IMG } from "../data/site";

export default function Gallery() {
  const { t } = useLang();
  useSeo("Gallery | Sparko Cleaning Netherlands", "Browse Sparko's professional cleaning gallery with before & after results and category filters.");
  const { data: gallery } = useContent("gallery");
  const [cat, setCat] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const cats = useMemo(() => ["all", ...new Set(gallery.map((g) => g.category))], [gallery]);
  const shown = cat === "all" ? gallery : gallery.filter((g) => g.category === cat);

  return (
    <div>
      <PageHero title="Gallery" crumb={t.nav.gallery} bg={IMG.deep}
        sub="A glimpse of the Sparko standard — spotless spaces and stunning transformations." />

      <section className="mx-auto max-w-6xl px-5 md:px-8 py-16">
        <h2 className="font-display text-2xl text-navy mb-6">{t.sections.beforeAfter}</h2>
        <BeforeAfter className="aspect-[16/9] shadow-xl" />
      </section>

      <section className="mx-auto max-w-7xl px-5 md:px-8 pb-24">
        <div className="flex flex-wrap gap-2 justify-center mb-10" data-testid="gallery-filters">
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`gfilter-${c}`}
              className={`rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors ${cat === c ? "bg-navy text-white" : "bg-muted text-navy hover:bg-blue/10"}`}
              style={cat !== c ? { background: "var(--muted)" } : {}}>{c}</button>
          ))}
        </div>
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [&>*]:mb-5">
          {shown.map((g, i) => (
            <motion.button key={g.id} layout initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
              onClick={() => setLightbox(g)} data-testid={`gallery-item-${i}`}
              className="group relative block w-full overflow-hidden rounded-3xl">
              <LazyImg src={fileUrl(g.image)} alt={g.title} className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/50 transition-colors grid place-items-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-center">
                  <Icon name="Expand" size={26} className="mx-auto" />
                  <p className="mt-2 font-display">{g.title}</p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)} data-testid="lightbox"
            className="fixed inset-0 z-[90] grid place-items-center bg-navy/95 backdrop-blur-md p-6">
            <button className="absolute top-6 end-6 text-white" onClick={() => setLightbox(null)}><Icon name="X" size={30} /></button>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="max-w-4xl w-full">
              <img src={fileUrl(lightbox.image)} alt={lightbox.title} className="w-full max-h-[80vh] object-contain rounded-2xl" />
              <p className="mt-4 text-center text-white font-display text-xl">{lightbox.title}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <CTABand />
    </div>
  );
}
